import { useEffect, useMemo, useRef, useState } from "react";
import KlineChart from "./components/KlineChart";
import { FALLBACK_RULES, fetchExchangeRules, fetchTickerPrice } from "./lib/binance";
import {
  calculateRisk,
  formatByPrecision,
  roundToTick,
  stopPriceFromPercent,
} from "./lib/calculator";
import {
  clearHistory,
  exportHistory,
  importHistory,
  loadHistory,
  savePlan,
} from "./lib/storage";
import type {
  Direction,
  MarginMode,
  MarketStatus,
  RiskInputs,
  SavedPlan,
  SymbolRules,
} from "./types";

type Tab = "create" | "chart" | "history";
type StopMode = "percent" | "price";

const DEFAULT_SYMBOL = "BTWUSDT";

function nowTime(): string {
  return new Date().toLocaleTimeString("zh-CN", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
}

function formatDate(timestamp: number): string {
  return new Date(timestamp).toLocaleString("zh-CN", {
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function createManualRules(symbol: string): SymbolRules {
  const normalized = symbol.toUpperCase();
  const baseAsset = normalized.endsWith("USDT") ? normalized.slice(0, -4) : normalized;
  return { ...FALLBACK_RULES, symbol: normalized, baseAsset: baseAsset || "COIN" };
}

export default function App() {
  const [tab, setTab] = useState<Tab>("create");
  const [symbol, setSymbol] = useState(DEFAULT_SYMBOL);
  const [direction, setDirection] = useState<Direction>("long");
  const [riskBudget, setRiskBudget] = useState("10");
  const [stopMode, setStopMode] = useState<StopMode>("percent");
  const [stopPercent, setStopPercent] = useState("5");
  const [stopPrice, setStopPrice] = useState("");
  const [rewardRiskRatio, setRewardRiskRatio] = useState("2");
  const [takeProfitLinked, setTakeProfitLinked] = useState(true);
  const [maxMargin, setMaxMargin] = useState("20");
  const [marginMode, setMarginMode] = useState<MarginMode>("isolated");
  const [entryPrice, setEntryPrice] = useState("");
  const [rules, setRules] = useState<SymbolRules[]>([]);
  const [marketStatus, setMarketStatus] = useState<MarketStatus>("idle");
  const [marketMessage, setMarketMessage] = useState("正在准备公开行情…");
  const [priceTime, setPriceTime] = useState("");
  const [activePlan, setActivePlan] = useState<SavedPlan | null>(null);
  const [history, setHistory] = useState<SavedPlan[]>(() => loadHistory());
  const [formError, setFormError] = useState("");
  const [dragging, setDragging] = useState(false);
  const [historyQuery, setHistoryQuery] = useState("");
  const [historyFilter, setHistoryFilter] = useState<"all" | Direction>("all");
  const importInputRef = useRef<HTMLInputElement>(null);

  const normalizedSymbol = symbol.trim().toUpperCase();
  const currentRules = useMemo(
    () => rules.find((item) => item.symbol === normalizedSymbol) ?? createManualRules(normalizedSymbol),
    [normalizedSymbol, rules],
  );

  const computedStopPrice = useMemo(() => {
    const entry = Number(entryPrice);
    const percent = Number(stopPercent);
    if (!(entry > 0) || !(percent > 0)) return 0;
    return roundToTick(stopPriceFromPercent(entry, percent, direction), currentRules.priceTick);
  }, [currentRules.priceTick, direction, entryPrice, stopPercent]);

  useEffect(() => {
    let cancelled = false;
    setMarketStatus("loading");
    setMarketMessage("正在读取币安 U 本位永续交易规则…");
    fetchExchangeRules()
      .then((items) => {
        if (cancelled) return;
        setRules(items);
        setMarketMessage(`已读取 ${items.length} 个 USDT 永续交易对`);
      })
      .catch((error) => {
        if (cancelled) return;
        setMarketStatus("error");
        setMarketMessage(error instanceof Error ? error.message : "交易规则加载失败，可改用手动价格");
      });
    return () => { cancelled = true; };
  }, []);

  const refreshPrice = async () => {
    if (!normalizedSymbol) return;
    setMarketStatus("loading");
    setMarketMessage(`正在查询 ${normalizedSymbol} 最新价…`);
    setFormError("");
    try {
      const price = await fetchTickerPrice(normalizedSymbol);
      setEntryPrice(String(price));
      setPriceTime(nowTime());
      setMarketStatus("live");
      setMarketMessage("币安公开行情 · 浏览器直连");
    } catch (error) {
      setMarketStatus("error");
      setMarketMessage(error instanceof Error ? error.message : "行情查询失败，请输入手动参考价");
    }
  };

  useEffect(() => {
    if (rules.length === 0) return;
    const exact = rules.some((item) => item.symbol === normalizedSymbol);
    if (!exact) {
      setMarketStatus("manual");
      setMarketMessage("未找到该 USDT 永续交易对，请检查名称或输入手动参考价");
      return;
    }
    const timer = window.setTimeout(() => void refreshPrice(), 250);
    return () => window.clearTimeout(timer);
    // 只有用户切换交易对或首次拿到规则时自动抓取一次，输入其他参数不会刷新并扰动快照。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [normalizedSymbol, rules.length]);

  useEffect(() => {
    if (stopMode === "price" && computedStopPrice > 0 && !stopPrice) {
      setStopPrice(String(computedStopPrice));
    }
  }, [computedStopPrice, stopMode, stopPrice]);

  const useManualPrice = () => {
    setMarketStatus("manual");
    setMarketMessage("手动价格模式 · 请自行核对时效");
    if (!entryPrice) setEntryPrice("0.27507");
  };

  const buildPlan = (overrides?: Partial<RiskInputs>, existingId?: string): SavedPlan => {
    const entry = Number(entryPrice);
    const requestedStop = stopMode === "percent" ? computedStopPrice : Number(stopPrice);
    const finalRules = rules.find((item) => item.symbol === normalizedSymbol) ?? createManualRules(normalizedSymbol);
    const inputs: RiskInputs = {
      symbol: normalizedSymbol,
      direction,
      entryPrice: entry,
      stopPrice: roundToTick(requestedStop, finalRules.priceTick),
      riskBudget: Number(riskBudget),
      rewardRiskRatio: Number(rewardRiskRatio),
      takeProfitLinked,
      maxMargin: Number(maxMargin),
      marginMode,
      ...overrides,
    };
    const result = calculateRisk(inputs, finalRules);
    const now = Date.now();
    return {
      id: existingId ?? crypto.randomUUID(),
      createdAt: existingId && activePlan?.id === existingId ? activePlan.createdAt : now,
      updatedAt: now,
      inputs,
      result,
      rules: finalRules,
      source: marketStatus === "live" ? "binance" : "manual",
    };
  };

  const generatePlan = () => {
    setFormError("");
    try {
      const plan = buildPlan();
      setActivePlan(plan);
      setTab("chart");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      setFormError(error instanceof Error ? error.message : "参数无法计算");
    }
  };

  const updateStopFromChart = (nextStop: number) => {
    if (!activePlan) return;
    try {
      const inputs = { ...activePlan.inputs, stopPrice: nextStop };
      setActivePlan({
        ...activePlan,
        inputs,
        result: calculateRisk(inputs, activePlan.rules),
        updatedAt: Date.now(),
      });
    } catch {
      // 图表组件已经限制止损不能穿过开仓价，这里仅防止极端坐标产生无效状态。
    }
  };

  const applyPlan = () => {
    if (!activePlan) return;
    const updated = { ...activePlan, updatedAt: Date.now() };
    setActivePlan(updated);
    setHistory(savePlan(updated));
    setMarketMessage("方案已保存到当前浏览器");
  };

  const openSavedPlan = (plan: SavedPlan) => {
    setActivePlan(plan);
    setSymbol(plan.inputs.symbol);
    setDirection(plan.inputs.direction);
    setEntryPrice(String(plan.inputs.entryPrice));
    setRiskBudget(String(plan.inputs.riskBudget));
    setStopMode("price");
    setStopPrice(String(plan.inputs.stopPrice));
    setStopPercent(String(plan.result.stopPercent));
    setRewardRiskRatio(String(plan.inputs.rewardRiskRatio));
    setTakeProfitLinked(plan.inputs.takeProfitLinked !== false);
    setMaxMargin(String(plan.inputs.maxMargin));
    setMarginMode(plan.inputs.marginMode);
    setMarketStatus(plan.source === "binance" ? "live" : "manual");
    setPriceTime(formatDate(plan.updatedAt));
    setTab("chart");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const backToEdit = () => {
    if (activePlan) {
      setStopMode("price");
      setStopPrice(String(activePlan.inputs.stopPrice));
      setStopPercent(String(activePlan.result.stopPercent));
    }
    setTab("create");
  };

  const activeBaseAsset = activePlan?.rules.baseAsset ?? currentRules.baseAsset;
  const leveragePreview = activePlan?.result.suggestedLeverage ?? 10;
  const filteredHistory = useMemo(() => history.filter((plan) => {
    const matchesQuery = plan.inputs.symbol.toLowerCase().includes(historyQuery.trim().toLowerCase());
    const matchesDirection = historyFilter === "all" || plan.inputs.direction === historyFilter;
    return matchesQuery && matchesDirection;
  }), [history, historyFilter, historyQuery]);

  return (
    <div className="app-shell">
      <main>
        {tab === "create" && (
          <div className="page-stack create-page">
            <header className="screen-title create-title">
              <div>
                <h1>合约风险计算器</h1>
                <p>按最大亏损反推开仓数量</p>
              </div>
              <span>仅计算</span>
            </header>

            <section className="panel market-panel">
              <div className="market-contract-row">
                <label htmlFor="symbol">合约</label>
                <div className="plain-symbol-input">
                  <input
                    id="symbol"
                    list="symbols"
                    value={symbol}
                    onChange={(event) => setSymbol(event.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))}
                    autoComplete="off"
                    spellCheck={false}
                  />
                </div>
                <span className="perpetual-tag">永续</span>
              </div>
              <datalist id="symbols">
                {rules.map((item) => <option value={item.symbol} key={item.symbol} />)}
              </datalist>
              <div className="panel-divider" />
              <div className="reference-price-row">
                <span>开仓参考价</span>
                {marketStatus === "live" ? (
                  <strong>{entryPrice || "—"} <em>USDT</em></strong>
                ) : (
                  <div className="manual-price-row">
                    <input
                      inputMode="decimal"
                      aria-label="手动开仓参考价"
                      placeholder="输入参考价"
                      value={entryPrice}
                      onChange={(event) => setEntryPrice(event.target.value)}
                    />
                    <em>USDT</em>
                  </div>
                )}
                <div className="snapshot-state">
                  <span>🔒 {marketStatus === "live" ? "价格快照已锁定" : marketStatus === "manual" ? "手动参考价" : priceTime || "等待价格"}</span>
                  <button type="button" onClick={() => void refreshPrice()}>刷新</button>
                </div>
              </div>
              <div className="segmented direction-switch two">
                <button type="button" className={direction === "long" ? "active long" : ""} onClick={() => setDirection("long")}>↗ 开多</button>
                <button type="button" className={direction === "short" ? "active short" : ""} onClick={() => setDirection("short")}>↘ 开空</button>
              </div>
              {marketStatus === "error" && <button type="button" className="manual-mode-link" onClick={useManualPrice}>行情不可用，改用手动价格</button>}
            </section>

            <section className="panel risk-settings-panel">
              <div className="section-heading">
                <span className="section-icon">♢</span>
                <h2>风险设置</h2>
              </div>
              <label className="field-label" htmlFor="risk">最大可承受亏损</label>
              <div className="input-shell amount-input">
                <input id="risk" inputMode="decimal" value={riskBudget} onChange={(event) => setRiskBudget(event.target.value)} />
                <span>USDT</span>
              </div>
              <p className="field-help">固定风险金额，不是保证金</p>

              <label className="field-label">止损方式</label>
              <div className="segmented stop-mode-switch two">
                <button type="button" className={stopMode === "percent" ? "active amber" : ""} onClick={() => setStopMode("percent")}>止损比例</button>
                <button type="button" className={stopMode === "price" ? "active amber" : ""} onClick={() => { setStopMode("price"); setStopPrice(String(computedStopPrice || "")); }}>止损价格</button>
              </div>
              {stopMode === "percent" ? (
                <div className="stop-converter">
                  <div>
                    <small>逆向波动</small>
                    <div><input id="stop-percent" inputMode="decimal" value={stopPercent} onChange={(event) => setStopPercent(event.target.value)} /><b>%</b></div>
                  </div>
                  <span className="converter-arrow">→</span>
                  <div>
                    <small>止损价</small>
                    <strong>{computedStopPrice > 0 ? formatByPrecision(computedStopPrice, currentRules.priceTick ? Math.min(8, String(currentRules.priceTick).split(".")[1]?.length ?? 0) : 5) : "—"}</strong>
                  </div>
                  <em>USDT</em>
                </div>
              ) : (
                <div className="input-shell amount-input">
                  <input id="stop-price" aria-label="止损触发价格" inputMode="decimal" value={stopPrice} onChange={(event) => setStopPrice(event.target.value)} />
                  <span>USDT</span>
                </div>
              )}
            </section>

            <section className="panel take-profit-panel">
              <div className="section-heading with-toggle">
                <div><span className="section-icon">◎</span><h2>止盈联动</h2></div>
                <button type="button" className={`toggle ${takeProfitLinked ? "on" : ""}`} aria-pressed={takeProfitLinked} onClick={() => setTakeProfitLinked((value) => !value)}><span /></button>
              </div>
              <div className={`ratio-row ${takeProfitLinked ? "" : "disabled"}`}>
                <div><small>风险收益比</small><strong>1 : <input id="rr" aria-label="风险收益比" inputMode="decimal" value={rewardRiskRatio} disabled={!takeProfitLinked} onChange={(event) => setRewardRiskRatio(event.target.value)} /></strong></div>
                <span>{takeProfitLinked ? `预计盈利约 ${Math.max(0, Number(riskBudget) * Number(rewardRiskRatio)).toFixed(0)}U` : "不设置止盈线"}</span>
              </div>
            </section>

            <section className="panel funds-panel">
              <div className="section-heading">
                <span className="section-icon">▤</span>
                <h2>资金与杠杆</h2>
              </div>
              <label className="field-label" htmlFor="margin">最多占用保证金</label>
              <div className="input-shell amount-input">
                <input id="margin" inputMode="decimal" value={maxMargin} onChange={(event) => setMaxMargin(event.target.value)} />
                <span>USDT</span>
              </div>
              <div className="inline-option-row">
                <span>保证金模式</span>
                <div className="segmented two compact">
                  <button type="button" className={marginMode === "isolated" ? "active amber" : ""} onClick={() => setMarginMode("isolated")}>逐仓</button>
                  <button type="button" className={marginMode === "cross" ? "active amber" : ""} onClick={() => setMarginMode("cross")}>全仓</button>
                </div>
              </div>
              <div className="inline-option-row auto-leverage-row">
                <span>自动比较杠杆方案</span>
                <span className="toggle on static-toggle"><span /></span>
              </div>
              <div className="leverage-strip">
                {[5, 10, 20].map((leverage) => (
                  <div key={leverage} className={leverage === leveragePreview ? "recommended" : ""}>
                    <b>{leverage}×</b><small>约 {(Number(entryPrice || 0) * Math.max(1, Number(riskBudget || 0)) / Math.max(0.000001, Math.abs(Number(entryPrice || 0) - (computedStopPrice || Number(stopPrice || 0)))) / leverage).toFixed(2)}U</small>
                  </div>
                ))}
              </div>
            </section>

            {formError && <div className="error-banner" role="alert">{formError}</div>}
            <button type="button" className="primary-cta" onClick={generatePlan} disabled={marketStatus === "loading" || !entryPrice}>
              生成可拖动 K 线方案
            </button>
            <p className="device-note">♢ 行情由本设备直接获取 · 不连接币安账户</p>
          </div>
        )}

        {tab === "chart" && activePlan && (
          <div className="page-stack chart-page">
            <header className="chart-screen-title">
              <button type="button" className="back-button" onClick={backToEdit}>←</button>
              <h1>{activePlan.inputs.symbol} · 风险方案</h1>
              <span className="live-state"><i />价格刚刚更新</span>
            </header>

            <div className="risk-anchor-card">
              <div>♢ 最大亏损 <b>{activePlan.inputs.riskBudget.toFixed(0)}U</b> 已固定</div>
              <div>🔒 开仓价 <b>{formatByPrecision(activePlan.inputs.entryPrice, activePlan.result.pricePrecision)}</b> 已锁定</div>
            </div>

            <KlineChart plan={activePlan} onStopChange={updateStopFromChart} onDraggingChange={setDragging} />

            <section className={`panel result-panel ${dragging ? "is-live-updating" : ""}`}>
              <div className="result-heading">
                <h2>{dragging ? "实时反推中" : "实时反推结果"}</h2>
                <small>{dragging ? "松手后按交易精度吸附" : "已按交易步长向下取整"}</small>
              </div>
              <div className="result-grid">
                <div><small>建议数量</small><strong>{formatByPrecision(activePlan.result.quantity, activePlan.result.quantityPrecision)} <em>{activeBaseAsset}</em></strong></div>
                <div><small>仓位价值</small><strong>{activePlan.result.notional.toFixed(2)} <em>USDT</em></strong></div>
                <div className="loss-result"><small>预计止损亏损</small><strong>{activePlan.result.actualRisk.toFixed(3)} <em>USDT</em></strong></div>
                <div className="profit-result"><small>止盈目标</small><strong>{activePlan.inputs.takeProfitLinked === false ? "未联动" : `约${activePlan.result.expectedReward.toFixed(2)}`} <em>{activePlan.inputs.takeProfitLinked === false ? "" : "USDT"}</em></strong></div>
              </div>
              <div className="leverage-summary">
                <span>满足 {activePlan.inputs.maxMargin.toFixed(0)}U 保证金上限</span>
                <b>建议 <em>{activePlan.result.suggestedLeverage}×</em></b>
                <span>预计保证金 <strong>{activePlan.result.estimatedMargin.toFixed(2)}U</strong></span>
              </div>
              <p className="snap-note">松手后按交易对价格与数量精度自动吸附</p>
              <div className="chart-actions">
                <button type="button" className="secondary-button" onClick={backToEdit}>撤销并返回</button>
                <button type="button" className="primary-button" onClick={applyPlan}>应用调整</button>
              </div>
            </section>

            {activePlan.result.warnings.length > 0 && (
              <section className="warning-list">
                {activePlan.result.warnings.map((warning) => <p key={warning}>⚠ {warning}</p>)}
              </section>
            )}
          </div>
        )}

        {tab === "history" && (
          <div className="page-stack history-page">
            <header className="history-title"><h1>风险方案</h1><p>仅保存在此设备 · 不是交易记录</p></header>
            <div className="history-search"><span>⌕</span><input aria-label="搜索合约" placeholder="搜索合约" value={historyQuery} onChange={(event) => setHistoryQuery(event.target.value)} /></div>
            <div className="history-filters">
              <button type="button" className={historyFilter === "all" ? "active" : ""} onClick={() => setHistoryFilter("all")}>全部</button>
              <button type="button" className={historyFilter === "long" ? "active" : ""} onClick={() => setHistoryFilter("long")}>开多</button>
              <button type="button" className={historyFilter === "short" ? "active" : ""} onClick={() => setHistoryFilter("short")}>开空</button>
            </div>

            {history.length === 0 ? (
              <section className="empty-state"><div>▦</div><h3>还没有保存的方案</h3><p>生成 K 线方案，拖好止损线后点击“应用调整”。</p><button type="button" onClick={() => setTab("create")}>新建方案</button></section>
            ) : (
              <div className="history-list">
                {filteredHistory.map((plan) => (
                  <article className="history-card" key={plan.id}>
                    <div className="history-card-top">
                      <div><span className="token-badge">{plan.rules.baseAsset.slice(0, 1)}</span><strong>{plan.inputs.symbol}</strong><span className={plan.inputs.direction}>{plan.inputs.direction === "long" ? "开多" : "开空"}</span></div>
                      <small>{formatDate(plan.updatedAt)}</small>
                    </div>
                    <div className="history-price-track">
                      <div><small>止盈</small><b>{plan.inputs.takeProfitLinked === false ? "—" : formatByPrecision(plan.result.takeProfitPrice, plan.result.pricePrecision)}</b></div>
                      <span className="track-line profit" />
                      <div><small>开仓</small><b>{formatByPrecision(plan.inputs.entryPrice, plan.result.pricePrecision)}</b></div>
                      <span className="track-line loss" />
                      <div><small>止损</small><b>{formatByPrecision(plan.inputs.stopPrice, plan.result.pricePrecision)}</b></div>
                    </div>
                    <div className="history-metrics">
                      <div><small>最大亏损</small><b>{plan.inputs.riskBudget.toFixed(2)}U</b></div>
                      <div><small>止损</small><b>{plan.result.stopPercent.toFixed(2)}%</b></div>
                      <div><small>数量</small><b>{formatByPrecision(plan.result.quantity, plan.result.quantityPrecision)} {plan.rules.baseAsset}</b></div>
                      <div><small>仓位</small><b>{plan.result.notional.toFixed(2)}U</b></div>
                    </div>
                    <div className="history-details"><span>风险收益比 <b>1:{plan.inputs.rewardRiskRatio}</b></span><span>建议杠杆 <b>{plan.result.suggestedLeverage}× · 保证金约{plan.result.estimatedMargin.toFixed(2)}U</b></span></div>
                    <div className="history-card-actions">
                      <button type="button" onClick={() => openSavedPlan(plan)}>⌁ 打开图表继续调整</button>
                      <button type="button" onClick={() => void navigator.clipboard.writeText(formatByPrecision(plan.result.quantity, plan.result.quantityPrecision))}>▣ 复制数量</button>
                    </div>
                  </article>
                ))}
                {filteredHistory.length === 0 && <div className="no-search-results">没有匹配的方案</div>}
              </div>
            )}
            <p className="history-note">♢ 保存的是计算快照；重新打开时会刷新行情，原开仓价仍可保留。</p>
            <div className="history-footer-actions">
              <button type="button" onClick={() => exportHistory(history)} disabled={history.length === 0}>⇧ 导出记录</button>
              <button type="button" onClick={() => importInputRef.current?.click()}>⇩ 导入记录</button>
              <button type="button" onClick={() => { if (window.confirm("确定清空当前浏览器内的全部风险方案吗？此操作不可恢复。")) { clearHistory(); setHistory([]); } }}>♜ 清空历史</button>
            </div>
            <input
              ref={importInputRef}
              className="visually-hidden"
              type="file"
              accept="application/json,.json"
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (!file) return;
                importHistory(file).then(setHistory).catch((error) => setMarketMessage(error instanceof Error ? error.message : "导入失败"));
                event.target.value = "";
              }}
            />
          </div>
        )}
      </main>

      <nav className="bottom-nav" aria-label="主导航">
        <button type="button" className={tab === "create" ? "active" : ""} onClick={() => setTab("create")}><span>▦</span>计算</button>
        <button type="button" className={tab === "chart" ? "active" : ""} onClick={() => activePlan && setTab("chart")} disabled={!activePlan}><span>▥</span>图表</button>
        <button type="button" className={tab === "history" ? "active" : ""} onClick={() => setTab("history")}><span>◷</span>历史</button>
      </nav>
    </div>
  );
}
